// Reordering maths for the Press Run Sheet.
//
// The run sheet is the lane read top-to-bottom in the order it will run, so
// dragging a row up or down IS a schedule change — the same `sequence` move the
// board's drag-drop and ←/→ keys make. This module holds the two pure pieces of
// that: which sequence value to PATCH, and where the rows land while the write
// is in flight.
//
// Two rules the maths has to respect, both inherited from the board:
//
//  1. Only PLANNED work reorders. A running job holds the press and a completed
//     one is history; neither may be dragged, and nothing may be dropped above
//     them. (compute_projection cascades in REALITY order for exactly this
//     reason — see the sort comment in PressRunSheetDialog.)
//  2. The target is a SEQUENCE VALUE, not a positional index. Completed rows on
//     a lane can carry sequence numbers that bear no relation to where they sit
//     on screen, so an index would be mistranslated by the server. Sending the
//     sequence currently sitting at the destination position is unambiguous:
//     PATCH /schedule shifts the rows between old and new by ±1, which realises
//     exactly that permutation whether or not the lane is densely numbered.
import { DowntimeWindow } from '../productionTypes';
import { splitRunAroundDowntime } from '../productionUtils';
import { GanttEntry } from './ganttTypes';

const MS_MIN = 60_000;

/** Only not-yet-started work can be moved. */
export const isReorderable = (e: GanttEntry) => e.status === 'planned';

/** Work that physically holds the press: nothing may be scheduled before it. */
const holdsPress = (e: GanttEntry) => e.status === 'in_progress' || e.status === 'completed';

/** Immutable array move. `to` is the index in the array AFTER removal. */
export function moveItem<T>(arr: T[], from: number, to: number): T[] {
  const next = arr.slice();
  if (from < 0 || from >= arr.length || to < 0 || to >= arr.length || from === to) return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * A drop lands "before row k" (k = 0..len). Translate that gap index into the
 * index the dragged row ends up at once it's been lifted out of the list.
 */
export const finalIndex = (from: number, insertAt: number) =>
  (insertAt > from ? insertAt - 1 : insertAt);

/**
 * The `sequence` to PATCH so the row at `from` ends up at `to`.
 *
 * The planned rows occupy some set of sequence values; reordering only permutes
 * which row holds which. So the target is simply the `to`-th smallest sequence
 * in the set. Returns null when nothing would move.
 */
export function sequenceForMove(planned: GanttEntry[], from: number, to: number): number | null {
  if (from === to) return null;
  if (from < 0 || from >= planned.length || to < 0 || to >= planned.length) return null;
  const seqs = planned.map((e) => e.sequence).sort((a, b) => a - b);
  const target = seqs[to];
  return target === planned[from].sequence ? null : target;
}

/**
 * Where the planned queue starts, in minutes from board base: the head of the
 * current plan, never earlier than now or than the end of work already run.
 * Reordering permutes the queue; it does not move its anchor.
 */
export function queueAnchor(laneEntries: GanttEntry[], nowAbs: number): number {
  const historyEnd = laneEntries
    .filter(holdsPress)
    .reduce((m, e) => Math.max(m, e.absEnd), -Infinity);
  const floor = Math.max(nowAbs, historyEnd === -Infinity ? nowAbs : historyEnd);
  const head = laneEntries
    .filter(isReorderable)
    .reduce((m, e) => Math.min(m, e.absStart), Infinity);
  return head === Infinity ? floor : Math.max(head, floor);
}

export interface CascadeArgs {
  /** Planned rows in their NEW order. */
  ordered: GanttEntry[];
  /** Queue anchor, minutes from base (see queueAnchor). */
  anchorAbs: number;
  /** THIS lane's raw downtime rules — downtime is per press. */
  windows: DowntimeWindow[];
  /** Board base (today 00:00) as epoch ms. */
  baseMs: number;
}

/**
 * Provisional start/end for each row after a reorder, packed nose-to-tail from
 * the anchor and paused across closures — the same walk adaptEntry does, so the
 * times shown while the PATCH is in flight agree with the ones that come back.
 *
 * Provisional, not authoritative: the per-entry run-through-downtime and
 * re-makeready flags don't survive into GanttEntry, so a flagged job's pause is
 * approximated here. The caller marks these times as unconfirmed and blocks
 * printing until the server's cascade lands.
 */
export function cascadeStarts(
  { ordered, anchorAbs, windows, baseMs }: CascadeArgs,
): Map<number, { absStart: number; absEnd: number }> {
  const out = new Map<number, { absStart: number; absEnd: number }>();
  let cursor = anchorAbs;
  for (const e of ordered) {
    const dur = e.setupMin + e.runMin + e.washupMin;
    const split = splitRunAroundDowntime(
      new Date(baseMs + cursor * MS_MIN).toISOString(), dur, windows,
    );
    const absEnd = (split.effectiveEnd.getTime() - baseMs) / MS_MIN;
    out.set(e.id, { absStart: cursor, absEnd });
    cursor = absEnd;
  }
  return out;
}

/** Apply a pending id order to the server's rows; ids that no longer exist drop out. */
export function applyPendingOrder(rows: GanttEntry[], ids: number[]): GanttEntry[] {
  const byId = new Map(rows.map((e) => [e.id, e]));
  const out: GanttEntry[] = [];
  for (const id of ids) {
    const e = byId.get(id);
    if (e) { out.push(e); byId.delete(id); }
  }
  // Anything the pending order didn't mention (a row added since) keeps its
  // server position at the tail rather than vanishing from the sheet.
  rows.forEach((e) => { if (byId.has(e.id)) out.push(e); });
  return out;
}

export const sameOrder = (a: number[], b: number[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);
